import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const STEPS = [
  // ── Home — nav overview ───────────────────────────────────────────────────
  { path: "/",          selector: null,                       title: "👋 Welcome to CardStoard", text: "This tour walks you through the key features using a real demo collection. It takes about 2 minutes." },
  { path: "/",          selector: '[title="View your collection"]', title: "📇 Cards",    text: "Browse your full collection. Sort by any column, filter by player, year, brand, or grade." },
  { path: "/",          selector: '[title="Browse card photos"]',   title: "🎴 Gallery",  text: "View card photos front and back. Click a card to flip it, double-click for full card detail." },
  { path: "/",          selector: '[title="Analytics"]',            title: "📊 Analytics", text: "See your collection value by player, brand, and era. Click any row in the tables to filter the card list." },

  // ── Cards deep dive ───────────────────────────────────────────────────────
  { path: "/list-cards", selector: "[data-tour='col-last']",        title: "Filtering by Player",   text: "Click the 🔍 icon next to any column header to filter. Type a last name to narrow the list instantly. Filters combine — player + grade at the same time." },
  { path: "/list-cards", selector: "[data-tour='col-grade']",       title: "Grade",                 text: "Every card is graded: 3.0 NM-MT+, 1.5 NM, 1.0 EX, 0.8 VG, 0.4 FR, 0.2 PR. Click the column to sort. Hover any grade badge on a card row for the full label." },
  { path: "/list-cards", selector: "[data-tour='col-book']",        title: "Book Values",           text: "Reference prices across five tiers: High, High-Mid, Mid, Low-Mid, Low. These drive the calculated card value." },
  { path: "/list-cards", selector: "[data-tour='col-value']",       title: "Card Value",            text: "Calculated from book values, condition grade, and CardStoard's market formulas. Updates whenever book values and/or market conditions change." },
  { path: "/list-cards", selector: "[data-tour='sort-btn']",        title: "⇅ Advanced Sort",       text: "Build a multi-level sort — up to 9 levels, each with its own column and direction. Save it as your default sort." },
  { path: "/list-cards", selector: "[data-tour='toolbar-count']",   zoom: 1.6, title: "Card Summary", text: "Always shows the current card count and total card value — accurate across your full filtered set." },

  // ── Gallery deep dive ─────────────────────────────────────────────────────
  { path: "/gallery",    selector: "[data-tour='gallery-search']",                   title: "🔍 Search Gallery", text: "Filter the gallery by player name, year, or brand. The gallery updates instantly as you type." },
  { path: "/gallery",    selector: "[data-tour='gallery-card']",    position: "above", title: "Card Photos",       text: "Click the card to flip it and see the back. Use the ◀ ▶ arrows to navigate through your collection." },
  { path: "/gallery",    selector: "[data-tour='gallery-detail']",  position: "above", title: "→ Card Detail",     text: "Open the full card detail view — grade, book values, value history, and all card attributes." },

  // ── Analytics deep dive ───────────────────────────────────────────────────
  { path: "/analytics",  selector: "[data-tour='analytics-trend']", position: "above", title: "Collection Trends",    text: "The Inventory chart shows how many cards were added over time. Once you revalue your collection, Valuation and Combined trends appear here too." },
  { path: "/analytics",  selector: "[data-tour='analytics-brand']", position: "above", title: "By Brand",  text: "Card count and total value grouped by manufacturer. Click any brand row to instantly filter the card list." },
  { path: "/analytics",  selector: "[data-tour='analytics-year']",  position: "above", title: "By Year",   text: "See which eras make up your collection and how value distributes across decades. Click any year to filter." },
  { path: "/analytics",  selector: "[data-tour='analytics-player']",position: "above", title: "By Player", text: "Your most represented players ranked by card count and total value. Click any player to filter the card list." },

  // ── CTA ───────────────────────────────────────────────────────────────────
  { path: null, selector: null, width: 420, title: "Ready to track your own collection?", text: "Sign up free — it takes 30 seconds. You'll get your own collection, book values, analytics, and card photos.", cta: true },
];

const PADDING = 10;
const BUBBLE_W = 360;
const MARGIN   = 12;

export default function DemoTour() {
  const [open, setOpen]   = useState(false);
  const [step, setStep]   = useState(0);
  const [rect, setRect]   = useState(null);
  const navigate          = useNavigate();
  const location          = useLocation();
  const retryRef          = useRef(null);
  const { logout }        = useAuth();

  useEffect(() => {
    const handler = () => { setStep(0); setRect(null); setOpen(true); };
    window.addEventListener("demo-start-tour", handler);
    return () => window.removeEventListener("demo-start-tour", handler);
  }, []);

  const current = STEPS[step];

  // Measure the target element, retry until found (handles post-navigation render delay)
  const startMeasure = (selector) => {
    clearInterval(retryRef.current);
    if (!selector) { setRect(null); return; }
    retryRef.current = setInterval(() => {
      const el = document.querySelector(selector);
      if (!el) return;
      clearInterval(retryRef.current);
      const r = el.getBoundingClientRect();
      setRect({ top: r.top - PADDING, left: r.left - PADDING, width: r.width + PADDING * 2, height: r.height + PADDING * 2 });
    }, 80);
  };

  useEffect(() => () => clearInterval(retryRef.current), []);

  // When step changes: navigate if needed, then start measuring
  useEffect(() => {
    setRect(null);
    if (current.path && location.pathname !== current.path) {
      navigate(current.path);
    }
    startMeasure(current.selector);
  }, [step]); // eslint-disable-line

  // Re-measure when navigation completes and we land on the right page
  useEffect(() => {
    if (current.path && location.pathname === current.path) {
      startMeasure(current.selector);
    }
  }, [location.pathname]); // eslint-disable-line

  // Zoom target element
  useEffect(() => {
    if (!rect || !current.selector) return;
    const el = document.querySelector(current.selector);
    if (!el) return;
    const parent = el.parentElement;
    el.style.transform   = `scale(${current.zoom || 1.25})`;
    el.style.transition  = "transform 0.25s ease";
    el.style.zIndex      = "9993";
    el.style.position    = "relative";
    if (parent) parent.style.overflow = "visible";
    return () => {
      el.style.transform  = "";
      el.style.transition = "";
      el.style.zIndex     = "";
      el.style.position   = "";
      if (parent) parent.style.overflow = "";
    };
  }, [rect, current.selector]);

  const advance = (delta) => {
    const next = step + delta;
    if (next < 0) return;
    if (next >= STEPS.length) { skip(); return; }
    clearInterval(retryRef.current);
    setStep(next);
  };

  const skip = () => { clearInterval(retryRef.current); sessionStorage.setItem("demo-tour-done", "1"); setOpen(false); };

  // Bubble position
  const bubbleStyle = (() => {
    if (!rect) return { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    const mid = rect.left + rect.width / 2;
    const vw  = window.innerWidth;
    let left  = mid > vw / 2
      ? Math.max(rect.left + rect.width - BUBBLE_W, MARGIN)
      : Math.max(mid - BUBBLE_W / 2, MARGIN);
    left = Math.min(left, vw - BUBBLE_W - MARGIN);
    const top = current.position === "above"
      ? rect.top - 300
      : rect.top + rect.height + 14;
    return { position: "fixed", top: Math.max(top, MARGIN), left };
  })();

  if (!open) return null;

  return (
    <>
      <div style={{ position: "fixed", inset: 0, zIndex: 9990, background: rect ? "transparent" : "rgba(0,0,0,0.65)", pointerEvents: "none" }} />

      {rect && (
        <div style={{ position: "fixed", top: rect.top, left: rect.left, width: rect.width, height: rect.height, borderRadius: 8, boxShadow: "0 0 0 9999px rgba(0,0,0,0.65)", zIndex: 9991, pointerEvents: "none" }} />
      )}

      <div style={{ ...bubbleStyle, zIndex: 9994, background: "#fff", border: "2px solid #f59e0b", borderRadius: 12, padding: "1.25rem 1.5rem", width: current.width || BUBBLE_W, boxShadow: "0 8px 32px rgba(0,0,0,0.25)" }}>
        <div style={{ marginBottom: "0.5rem" }}>
          <span style={{ fontSize: "0.75rem", color: "#78350f", fontWeight: 700 }}>{step + 1} of {STEPS.length}</span>
        </div>

        <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: "0.5rem", color: "#1a1a1a" }}>{current.title}</div>
        <p style={{ margin: "0 0 1.25rem", fontSize: "0.9rem", color: "#444", lineHeight: 1.5 }}>{current.text}</p>

        <div style={{ display: "flex", justifyContent: "center", gap: "0.4rem", marginBottom: "1rem" }}>
          {STEPS.map((_, i) => <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: i === step ? "#f59e0b" : "#ddd" }} />)}
        </div>

        <div style={{ display: "flex", gap: "0.6rem", justifyContent: "center" }}>
          {step > 0 && (
            <button className="nav-btn" onClick={() => advance(-1)} style={{ fontSize: "0.85rem", background: "var(--bg-muted)", color: "var(--text-primary)", padding: "0.3rem 0.9rem" }}>← Back</button>
          )}
          {current.cta && (
            <button className="nav-btn" style={{ background: "#1a7a1a", fontSize: "0.9rem" }} onClick={async () => { setOpen(false); await logout(); navigate("/register"); }}>Sign Up Free</button>
          )}
          <button className="nav-btn" style={{ fontSize: "0.9rem", background: "#f59e0b", color: "#78350f" }} onClick={() => advance(1)}>
            {step < STEPS.length - 1 ? "Next →" : "Done"}
          </button>
        </div>
        <div style={{ textAlign: "right", marginTop: "0.6rem" }}>
          <button onClick={skip} style={{ background: "none", border: "none", color: "var(--btn-primary-bg)", cursor: "pointer", fontSize: "0.78rem", fontWeight: 600 }}>Exit Tour</button>
        </div>
      </div>
    </>
  );
}
